import { ScrollView, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ArrowLeft, BarChart3, FileText, Target, UploadCloud } from 'lucide-react-native';
import { AppButton } from '../components/ui/AppButton';
import { AppCard } from '../components/ui/AppCard';
import { AppText } from '../components/ui/AppText';
import { ScreenBackground } from '../components/ui/ScreenBackground';
import { tokens } from '../theme/tokens';
import { recordPilotFlowFinished, recordPilotFlowStarted } from '../utils/pilotTelemetry';

const analysisItems = [
  { label: '预计得分', value: '82', helper: '上传后生成本地 demo 报告' },
  { label: '错题数量', value: '6', helper: '识别后进入批改结果' },
  { label: '薄弱知识点', value: '3', helper: '可归档到考试详情' },
];

const reportSections = [
  '成绩分析和题型得分分布',
  '错题分布与知识点薄弱项',
  '推荐练习和归档后报告入口',
  '上传失败、识别失败、重新上传状态',
];

export default function PaperAnalysisPage() {
  const router = useRouter();

  const openUpload = () => {
    void recordPilotFlowStarted('paper_analysis');
    void recordPilotFlowFinished('paper_analysis', 'skipped');
    router.push('/exam-upload');
  };

  return (
    <SafeAreaView style={styles.screen}>
      <ScreenBackground style={styles.background}>
        <View style={styles.header}>
          <AppButton
            label="问答"
            variant="ghost"
            onPress={() => router.back()}
            leftSlot={<ArrowLeft size={18} color={tokens.colors.primary} />}
            style={styles.backButton}
          />
          <View style={styles.headerCopy}>
            <AppText variant="title" weight="bold">
              试卷分析
            </AppText>
            <AppText variant="caption" tone="muted">
              上传试卷后查看成绩、失分原因和薄弱点
            </AppText>
          </View>
        </View>

        <ScrollView contentContainerStyle={styles.content}>
          <AppCard style={styles.noticeCard}>
            <View style={styles.noticeIcon}>
              <FileText size={22} color={tokens.colors.primary} />
            </View>
            <View style={styles.noticeCopy}>
              <AppText variant="subtitle" weight="bold">
                试卷分析待设计确认
              </AppText>
              <AppText variant="body" tone="muted">
                先按 HTML 原型补齐上传、批改结果、归档详情三步 demo 流程，真实批改接口后续再接入。
              </AppText>
            </View>
          </AppCard>

          <View style={styles.metricsGrid}>
            {analysisItems.map((item) => (
              <AppCard key={item.label} style={styles.metricCard}>
                <AppText variant="metric" weight="bold" tone="primary">
                  {item.value}
                </AppText>
                <AppText variant="body" weight="bold">
                  {item.label}
                </AppText>
                <AppText variant="caption" tone="muted">
                  {item.helper}
                </AppText>
              </AppCard>
            ))}
          </View>

          <AppCard style={styles.sectionCard}>
            <View style={styles.sectionHeader}>
              <BarChart3 size={20} color={tokens.colors.primary} />
              <AppText variant="subtitle" weight="bold">
                报告结构
              </AppText>
            </View>
            {reportSections.map((item) => (
              <View key={item} style={styles.checkRow}>
                <Target size={16} color={tokens.colors.warning} />
                <AppText variant="body" tone="muted" style={styles.checkCopy}>
                  {item}
                </AppText>
              </View>
            ))}
          </AppCard>

          <View style={styles.actions}>
            <AppButton
              label="上传试卷"
              onPress={openUpload}
              leftSlot={<UploadCloud size={18} color={tokens.colors.white} />}
            />
            <AppButton
              label="查看归档报告"
              variant="secondary"
              onPress={() => router.push('/exam-detail')}
            />
          </View>
        </ScrollView>
      </ScreenBackground>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  background: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.sm,
  },
  backButton: {
    minHeight: 40,
    paddingHorizontal: tokens.spacing.sm,
  },
  headerCopy: {
    flex: 1,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.xxl,
    gap: tokens.spacing.lg,
  },
  noticeCard: {
    flexDirection: 'row',
    gap: tokens.spacing.md,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  noticeIcon: {
    width: 42,
    height: 42,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surface,
  },
  noticeCopy: {
    flex: 1,
    gap: tokens.spacing.sm,
  },
  metricsGrid: {
    flexDirection: 'row',
    gap: tokens.spacing.md,
  },
  metricCard: {
    flex: 1,
    gap: tokens.spacing.xs,
  },
  sectionCard: {
    gap: tokens.spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  checkRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: tokens.spacing.sm,
  },
  checkCopy: {
    flex: 1,
  },
  actions: {
    gap: tokens.spacing.md,
  },
});
